import React, { useCallback } from 'react';

import { FlatList, ListRenderItemInfo, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import CheckMarkImage from 'assets/images/svgs/ico_checkmark.svg';

import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { updateFavouritesChannelsList } from 'store/user/actions';

import useHeaderConfig from 'utils/hooks/useHeaderConfig';
import { isWeb } from 'utils/responsiveness';

import SettingsRowItem from 'screens/Settings/components/RowItem';
import WebHeader from 'screens/Settings/components/WebHeader';
import useChannelsLineups from 'screens/Settings/hooks/useChannelsLineups';
import {
    SETTINGS_CHANNEL_LINEUP,
    SETTINGS_CHANNEL_LINEUP_MY_LIST,
    SETTINGS_CHANNEL_LINEUP_STANDARD_LIST,
} from 'screens/Settings/languagesConst';
import { ChannelsLineup } from 'screens/Settings/types';

import { ETextColor } from 'components/Text';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.CHANNELS_LINEUP
    >;
};

function SwitchChannelList({ navigation }: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useHeaderConfig({ isRoot: isWeb, disableFocusOnMount: true });

    const { background } = useSelector(selectLayoutConfig);

    const { myList, channelsLineups } = useChannelsLineups();

    const handlePress = useCallback(
        (selectedItem: ChannelsLineup) => {
            const meta = {
                onSuccess: () => {
                    if (navigation.canGoBack()) {
                        navigation.goBack();
                    } else {
                        navigation.reset({
                            routes: [
                                {
                                    name: SCREEN.CHANNELS_LINEUP,
                                },
                            ],
                        });
                    }
                },
            };

            if (selectedItem.id === SETTINGS_CHANNEL_LINEUP_STANDARD_LIST) {
                dispatch(
                    updateFavouritesChannelsList(
                        {
                            ...myList,
                            tags: [''],
                        },
                        meta,
                    ),
                );
            } else {
                dispatch(
                    updateFavouritesChannelsList(
                        {
                            ...myList,
                            tags: ['active'],
                        },
                        meta,
                    ),
                );
            }
        },
        [navigation, myList],
    );

    const renderItem = useCallback(
        ({ item, index }: ListRenderItemInfo<ChannelsLineup>) => (
            <SettingsRowItem
                item={item}
                title={t(item.title)}
                onPress={handlePress}
                RightIcon={item.selected ? CheckMarkImage : undefined}
                disabled={
                    item.id === SETTINGS_CHANNEL_LINEUP_MY_LIST && !myList
                }
                titleColor={
                    item.id === SETTINGS_CHANNEL_LINEUP_MY_LIST && !myList
                        ? ETextColor.dark_grey
                        : ETextColor.neutral
                }
                position={index + 1}
                total={channelsLineups.length}
                isSelected={item.selected}
            />
        ),
        [t, handlePress, myList],
    );

    return (
        <>
            {isWeb && (
                <WebHeader
                    title={t(SETTINGS_CHANNEL_LINEUP)}
                    disableFocusOnMount
                />
            )}
            <View
                style={[
                    gStyles.settingsContainer,
                    { backgroundColor: background },
                ]}
            >
                <FlatList
                    style={[
                        gStyles.settingsContentContainer,
                        { backgroundColor: background },
                    ]}
                    data={channelsLineups}
                    renderItem={renderItem}
                    keyExtractor={keyExtractor}
                    accessibilityRole="menu"
                />
            </View>
        </>
    );
}

export default SwitchChannelList;

function keyExtractor(item: { id: string }) {
    return item.id;
}
